import { ModelActivitySchema } from "./model-collaboration.js";
import { FileInputFormatIssueSchema, FilePreparationOutcomeSchema } from "./files.js";
import { Type } from "@sinclair/typebox";
import { TerminationSchema } from "./lifecycle.js";
import { BudgetSchema } from "./action.js";
import { ToolJobProgressSchema } from "./tools.js";
import { ResultDocumentSchema } from "./result-document.js";
import { ContractValidationError, parseContract } from "../schema-helpers.js";
import { IdSchema, JsonValueSchema, NonNegativeIntegerSchema, Sha256Schema, TimestampSchema, } from "./shared.js";
export const WorkingPlanStepSchema = Type.Object({
    stepId: IdSchema,
    acceptanceCriteria: Type.Optional(Type.String({ minLength: 1, maxLength: 4000 })),
    title: Type.String({ minLength: 1, maxLength: 500 }),
    status: Type.Union([
        Type.Literal("pending"),
        Type.Literal("in_progress"),
        Type.Literal("completed"),
        Type.Literal("failed"),
        Type.Literal("cancelled"),
    ]),
    dependsOn: Type.Array(IdSchema, { maxItems: 128 }),
}, { additionalProperties: false });
export const WorkingPlanSchema = Type.Object({
    planId: IdSchema,
    sessionId: IdSchema,
    turnId: IdSchema,
    revision: NonNegativeIntegerSchema,
    digest: Sha256Schema,
    steps: Type.Array(WorkingPlanStepSchema, { maxItems: 256 }),
    createdAt: TimestampSchema,
}, { additionalProperties: false });
const TurnStatusSchema = Type.Union([
    Type.Literal("accepted"),
    Type.Literal("running"),
    Type.Literal("waiting_plan"),
    Type.Literal("waiting_approval"),
    Type.Literal("completed"),
    Type.Literal("failed"),
    Type.Literal("cancelled"),
]);
const EventGuideSchema = Type.Object({
    definitionId: IdSchema,
    title: Type.String({ minLength: 1, maxLength: 300 }),
    contentDigest: Sha256Schema,
    matchReason: Type.String({ minLength: 1, maxLength: 1_000 }),
}, { additionalProperties: false });
export const FunctionSelectionEvidenceSchema = Type.Object({
    serverId: IdSchema,
    functionName: Type.String({ minLength: 1, maxLength: 512 }),
    schemaDigest: Sha256Schema,
}, { additionalProperties: false });
export const ActionIntentEvidenceSchema = Type.Object({
    serverId: IdSchema,
    functionName: Type.String({ minLength: 1, maxLength: 512 }),
    argumentsDigest: Sha256Schema,
}, { additionalProperties: false });
export const ModelDecisionEvidenceFields = {
    requestId: IdSchema,
    decisionKind: Type.Union([
        Type.Literal("select_function"),
        Type.Literal("action_intent"),
        Type.Literal("replan"),
        Type.Literal("completed"),
        Type.Literal("paused"),
        Type.Literal("reply"),
    ]),
    semanticPayloadDigest: Sha256Schema,
    functionSelection: Type.Optional(FunctionSelectionEvidenceSchema),
    selectionRequestId: Type.Optional(IdSchema),
    bindingAttempt: Type.Optional(Type.Integer({ minimum: 1, maximum: 16 })),
    actionIntent: Type.Optional(ActionIntentEvidenceSchema),
    inputTokens: NonNegativeIntegerSchema,
    outputTokens: NonNegativeIntegerSchema,
};
export const ModelActionRejectionEvidenceFields = {
    requestId: IdSchema,
    semanticPayloadDigest: Sha256Schema,
    reason: Type.Union([
        Type.Literal("invalid_action_intent"),
        Type.Literal("invalid_function_selection"),
        Type.Literal("function_not_available"),
        Type.Literal("arguments_rejected"),
        Type.Literal("function_contract_drift"),
        Type.Literal("duplicate_completed_action"),
        Type.Literal("canary_execution_budget_exhausted"),
        Type.Literal("file_format_unknown"),
        Type.Literal("file_format_incompatible"),
    ]),
    selectionRequestId: Type.Optional(IdSchema),
    bindingAttempt: Type.Optional(Type.Integer({ minimum: 1, maximum: 16 })),
    serverId: Type.Optional(IdSchema),
    functionName: Type.Optional(Type.String({ minLength: 1, maxLength: 512 })),
    argumentsDigest: Type.Optional(Sha256Schema),
    fileInputs: Type.Optional(Type.Array(FileInputFormatIssueSchema, { minItems: 1, maxItems: 256 })),
    violations: Type.Optional(Type.Array(Type.Object({
        path: Type.String({ minLength: 1, maxLength: 512 }),
        keyword: Type.String({ minLength: 1, maxLength: 64 }),
        detail: Type.Optional(Type.String({ minLength: 1, maxLength: 256 })),
    }, { additionalProperties: false }), { maxItems: 8 })),
};
export const TaskEventPayloadSchemas = Object.freeze({
    "session.preparation_updated": Type.Object({
        state: Type.Union([
            Type.Literal("queued"),
            Type.Literal("running"),
            Type.Literal("reconciling"),
            Type.Literal("ready"),
            Type.Literal("failed"),
            Type.Literal("cancelled"),
            Type.Literal("needs_attention"),
        ]),
        phase: Type.Union([
            Type.Literal("queued"),
            Type.Literal("creating"),
            Type.Literal("verifying"),
            Type.Literal("stopping"),
            Type.Literal("closed"),
        ]),
        attempt: Type.Integer({ minimum: 0, maximum: 3 }),
        deadline: TimestampSchema,
        reason: Type.Union([Type.String({ pattern: "^[a-z][a-z0-9_]{0,127}$" }), Type.Null()]),
    }, { additionalProperties: false }),
    "session.termination_updated": TerminationSchema,
    "session.provisioning": Type.Object({
        status: Type.Literal("provisioning"),
        providerConfigDigest: Sha256Schema,
    }, { additionalProperties: false }),
    "session.active": Type.Object({ status: Type.Literal("active"), targetDigest: Sha256Schema }, { additionalProperties: false }),
    "session.rotating": Type.Object({
        requestId: IdSchema,
        fromGeneration: NonNegativeIntegerSchema,
        toGeneration: NonNegativeIntegerSchema,
        previousTargetDigest: Sha256Schema,
    }, { additionalProperties: false }),
    "session.generation_provisioning": Type.Object({
        fromGeneration: NonNegativeIntegerSchema,
        toGeneration: NonNegativeIntegerSchema,
        previousTargetDigest: Sha256Schema,
    }, { additionalProperties: false }),
    "session.failed": Type.Object({
        status: Type.Literal("failed"),
        reason: Type.Union([
            Type.Literal("runtime_provisioning_failed"),
            Type.Literal("mcp_execution_uncertain"),
        ]),
    }, { additionalProperties: false }),
    "turn.accepted": Type.Object({
        purpose: Type.Optional(Type.Union([
            Type.Literal("research"),
            Type.Literal("report"),
            Type.Literal("guide_review"),
        ])),
        requestId: IdSchema,
        goal: Type.String({ minLength: 1, maxLength: 100_000 }),
        planMode: Type.Boolean(),
        guide: Type.Union([EventGuideSchema, Type.Null()]),
    }, { additionalProperties: false }),
    "turn.transitioned": Type.Object({
        from: TurnStatusSchema,
        to: TurnStatusSchema,
        reason: Type.String({ minLength: 1, maxLength: 128 }),
    }, { additionalProperties: false }),
    "plan.updated": Type.Object({
        planId: IdSchema,
        revision: NonNegativeIntegerSchema,
        digest: Sha256Schema,
    }, { additionalProperties: false }),
    "model.activity": ModelActivitySchema,
    "model.decision": Type.Object(ModelDecisionEvidenceFields, { additionalProperties: false }),
    "model.action_rejected": Type.Object(ModelActionRejectionEvidenceFields, {
        additionalProperties: false,
    }),
    "action.requested": Type.Object({
        actionId: IdSchema,
        requestDigest: Sha256Schema,
        approvalClassDigest: Sha256Schema,
        functionName: Type.String({ minLength: 1, maxLength: 256 }),
        risk: Type.Union([Type.Literal("low"), Type.Literal("high")]),
        approvalReused: Type.Optional(Type.Boolean()),
    }, { additionalProperties: false }),
    "action.progress": Type.Object({
        actionId: IdSchema,
        grantId: IdSchema,
        serverId: IdSchema,
        functionName: Type.String({ minLength: 1, maxLength: 256 }),
        progress: ToolJobProgressSchema,
    }, { additionalProperties: false }),
    "action.completed": Type.Object({
        actionId: IdSchema,
        grantId: IdSchema,
        observationId: IdSchema,
        status: Type.Union([
            Type.Literal("succeeded"),
            Type.Literal("failed"),
            Type.Literal("cancelled"),
            Type.Literal("timed_out"),
        ]),
        resultDigest: Sha256Schema,
        result: JsonValueSchema,
        actualCost: BudgetSchema,
    }, { additionalProperties: false }),
    "file.preparation_completed": FilePreparationOutcomeSchema,
    "result.updated": Type.Object({
        resultDocument: ResultDocumentSchema,
    }, { additionalProperties: false }),
    "turn.completed": Type.Object({
        outcome: Type.Union([
            Type.Literal("completed"),
            Type.Literal("reply"),
            Type.Literal("paused"),
        ]),
        response: JsonValueSchema,
        resultDocument: Type.Optional(ResultDocumentSchema),
    }, { additionalProperties: false }),
    "turn.failed": Type.Object({
        code: Type.String({ minLength: 1, maxLength: 128 }),
        stage: Type.String({ minLength: 1, maxLength: 128 }),
        message: Type.String({ minLength: 1, maxLength: 2_000 }),
    }, { additionalProperties: false }),
    "session.deleting": Type.Object({ jobId: IdSchema, sessionId: IdSchema }, { additionalProperties: false }),
});
export const TaskEventKinds = Object.freeze(Object.keys(TaskEventPayloadSchemas));
const SessionTaskEventKinds = Object.freeze([
    "session.preparation_updated",
    "session.provisioning",
    "session.active",
    "session.rotating",
    "session.generation_provisioning",
    "session.failed",
    "session.termination_updated",
    "session.deleting",
]);
const sessionTaskEventKindSet = new Set(SessionTaskEventKinds);
const TaskEventBaseSchema = {
    cursor: Type.String({ pattern: "^[1-9][0-9]*$" }),
    eventId: IdSchema,
    sessionId: IdSchema,
    generation: NonNegativeIntegerSchema,
    payloadDigest: Sha256Schema,
    occurredAt: TimestampSchema,
};
const taskEventBranchSchemas = TaskEventKinds.map((kind) => Type.Object({
    ...TaskEventBaseSchema,
    ...(sessionTaskEventKindSet.has(kind) ? {} : { turnId: IdSchema }),
    kind: Type.Literal(kind),
    payload: TaskEventPayloadSchemas[kind],
}, { additionalProperties: false }));
const taskEventSchemaByKind = new Map(TaskEventKinds.map((kind, index) => [kind, taskEventBranchSchemas[index]]));
export const TaskEventSchema = Type.Union(taskEventBranchSchemas);
/** 保留闭合 union 作为线协议，同时让运行时按已登记 kind 精确校验唯一分支。 */
export function parseTaskEvent(input) {
    return parseContract(taskEventSchemaFor(input), input);
}
/** 所有运行环境均从同一闭合 union 选择分支，具体校验器不另建协议。 */
export function taskEventSchemaFor(input) {
    if (typeof input !== "object" || input === null || Array.isArray(input)) {
        throw new ContractValidationError(["/: TaskEvent 必须是对象"]);
    }
    const kind = input["kind"];
    const schema = typeof kind === "string" ? taskEventSchemaByKind.get(kind) : null;
    if (!schema)
        throw new ContractValidationError(["/kind: TaskEvent kind 未登记"]);
    return schema;
}
export const TaskMessageBodySchema = Type.Union([
    Type.Object({
        kind: Type.Literal("submit_turn"),
        requestId: IdSchema,
        sessionId: IdSchema,
        goal: Type.String({ minLength: 1, maxLength: 100_000 }),
        planMode: Type.Boolean(),
        guideDefinitionId: Type.Optional(Type.Union([IdSchema, Type.Null()])),
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("task_event"),
        event: TaskEventSchema,
    }, { additionalProperties: false }),
    Type.Object({
        kind: Type.Literal("working_plan"),
        plan: WorkingPlanSchema,
    }, { additionalProperties: false }),
]);
//# sourceMappingURL=task.js.map